import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { adminApiClient } from '../api/client';
import { Card, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Loading } from './ui/Loading';
import { X, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { formatTokens } from '../utils/format-tokens';

interface TelemetryDetailModalProps {
  analysisId: string;
  onClose: () => void;
}

const CHART_COLORS = ['#60a5fa', '#34d399', '#fbbf24', '#a78bfa', '#fb923c', '#f472b6', '#38bdf8', '#4ade80'];

const TelemetryDetailModal: React.FC<TelemetryDetailModalProps> = ({ analysisId, onClose }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['analysis-telemetry', analysisId],
    queryFn: () => adminApiClient.getAnalysisTelemetry(analysisId),
    retry: false,
  });

  const is404 = (error as any)?.response?.status === 404;

  const byModelData = data?.token_usage.by_model
    ? Object.entries(data.token_usage.by_model).map(([name, value]) => ({ name, value }))
    : [];

  const byStageData = data?.token_usage.by_stage
    ? Object.entries(data.token_usage.by_stage).map(([name, tokens]) => ({ name, tokens }))
    : [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>
      <Card className="w-full max-w-3xl max-h-[85vh] overflow-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div>
            <CardTitle>Analysis Telemetry</CardTitle>
            <p className="text-gray-400 text-sm mt-1 font-mono">{analysisId}</p>
            {data && (
              <p className="text-gray-500 text-xs mt-1">
                {format(new Date(data.timestamp), 'MMM d, yyyy HH:mm:ss')}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6">
          {isLoading && <Loading message="Loading telemetry..." />}

          {is404 && (
            <p className="text-center text-gray-400 py-8">
              No telemetry data available for this analysis.
            </p>
          )}

          {error && !is404 && (
            <p className="text-red-400">Failed to load telemetry data.</p>
          )}

          {data && (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-3 bg-gray-900 rounded-lg text-center">
                  <p className="text-lg font-semibold text-white">{formatTokens(data.token_usage.total_tokens)}</p>
                  <p className="text-xs text-gray-400">Total Tokens</p>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg text-center">
                  <p className="text-lg font-semibold text-blue-400">{formatTokens(data.token_usage.input_tokens)}</p>
                  <p className="text-xs text-gray-400">Input</p>
                  <p className="text-lg font-semibold text-green-400">{formatTokens(data.token_usage.output_tokens)}</p>
                  <p className="text-xs text-gray-400">Output</p>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg text-center">
                  <p className="text-lg font-semibold text-white">{data.token_usage.llm_call_count}</p>
                  <p className="text-xs text-gray-400">LLM Calls</p>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg text-center">
                  <p className="text-lg font-semibold text-white">
                    {data.token_usage.total_lines_analyzed != null
                      ? data.token_usage.total_lines_analyzed.toLocaleString()
                      : '—'}
                  </p>
                  <p className="text-xs text-gray-400">Lines Analyzed</p>
                  {data.token_usage.tokens_per_line != null && (
                    <p className="text-xs text-gray-500 mt-1">{data.token_usage.tokens_per_line.toFixed(1)} tok/line</p>
                  )}
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {byModelData.length > 0 && (
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-3">By Model</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={byModelData} dataKey="value" nameKey="name" cx="50%" cy="40%" outerRadius={60} label={false}>
                          {byModelData.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#d1d5db' }}
                          itemStyle={{ color: '#d1d5db' }}
                          formatter={(value: number) => formatTokens(value)}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {byStageData.length > 0 && (
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-3">By Stage</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={byStageData}>
                        <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} tickFormatter={formatTokens} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                          labelStyle={{ color: '#d1d5db' }}
                          formatter={(value: number) => formatTokens(value)}
                        />
                        <Bar dataKey="tokens" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Cache Stats */}
              {(data.token_usage.cache_creation_tokens > 0 || data.token_usage.cache_read_tokens > 0) && (
                <div className="bg-gray-900 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-2">Cache</h3>
                  <div className="flex space-x-6 text-sm">
                    <span className="text-gray-400">
                      Created: <span className="text-white">{formatTokens(data.token_usage.cache_creation_tokens)}</span>
                    </span>
                    <span className="text-gray-400">
                      Read: <span className="text-white">{formatTokens(data.token_usage.cache_read_tokens)}</span>
                    </span>
                    {data.token_usage.input_cached_tokens != null && (
                      <span className="text-gray-400">
                        Input Cached: <span className="text-white">{formatTokens(data.token_usage.input_cached_tokens)}</span>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-end">
                <Link
                  to={`/admin/telemetry/${analysisId}`}
                  className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-sm"
                  onClick={onClose}
                >
                  <span>View full details</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
            </>
          )}
        </div>
      </Card>
      </div>
    </div>
  );
};

export default TelemetryDetailModal;
